import { useEffect } from 'react';
import { Banner, Buttons, Sizes } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showChat } from 'Services/chat';
import { history } from 'Store';
import { useTranslation } from 'Hooks/useTranslation';
import {
  getIsFlexNotMandatory,
  getUserRedirectToPayLater,
  handleRedirectUser,
  sendTaxReturnResultViewEvent,
} from 'Containers/Result/utils';
import {
  useFetchGetUserProfile,
  useFetchRequestInstantRefund,
  useGetUserPlan,
} from 'Containers/Result/hooks';
import { ToReturnProps } from 'Containers/Result/types';
import PreviousYearInfo from 'Containers/Result/components/PreviousYearInfo';
import ResultLessRefund from 'Containers/Result/components/ResultLessRefund';
import { useGetPreviousYearInfo } from 'Containers/Filing/hooks';
import FlexsUsersResult from 'Containers/Result/components/FlexsUsersResult';
import { FRONT_PLANS } from 'Constants/plans';
import { routes } from 'Constants/routes';
import { TaxProfile, taxReturnFilingType } from 'Types/common';
import ResultInfo from '../ResultInfo';
import * as Styled from './styles';

const BREAK_RESULT_AMOUNT = 100;

const ResultBox = ({ data, flowEnabled, showPreviousYearBanner }: ToReturnProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const previousYearInfo = useGetPreviousYearInfo();
  const userComplex = useFetchGetUserProfile();
  const { data: instantRefundRequest, isLoading: loadingInstantRefund } =
    useFetchRequestInstantRefund();
  const plan = useGetUserPlan();

  const shouldRedirectToPayLater = getUserRedirectToPayLater({
    data,
    userComplex: userComplex?.taxProfile,
    plan,
  });

  const resultLessThan100 = Math.abs(data.taxReturnResult) < BREAK_RESULT_AMOUNT;
  const havePreviousYearsAndIsLessThan100 = showPreviousYearBanner && resultLessThan100;
  const isFlexNotMandatory = getIsFlexNotMandatory(data, previousYearInfo, showPreviousYearBanner);
  const isUserToPro = data && data?.flexToPro;

  const resultIsLessThan100AndNotHavePreviousYears =
    Math.abs(data.taxReturnResult) < BREAK_RESULT_AMOUNT &&
    !showPreviousYearBanner &&
    userComplex?.taxProfile === TaxProfile.SIMPLE;

  const redirectToFlexLite =
    resultIsLessThan100AndNotHavePreviousYears || havePreviousYearsAndIsLessThan100;
  const handleOnClick = (): void => {
    if (data?.isUserForFed && plan !== FRONT_PLANS.FLEX) {
      const route = plan === FRONT_PLANS.BASIC ? routes.RESULT_FILTER : routes.RESULT_EFIRMA;
      history.push(route, {
        route: routes.RESULT,
        section: 'result',
        taxReturnType: data?.taxReturnType,
        taxReturnResult: data?.taxReturnResult,
        instantRefund: instantRefundRequest,
        havePreviousYears: showPreviousYearBanner,
        isFlexNotMandatory,
      });
    } else if (shouldRedirectToPayLater) {
      const route = routes.PAYMENT_LATER;
      history.push(route, {
        route: routes.RESULT,
        section: 'result',
        taxReturnType: data?.taxReturnType,
        taxReturnResult: data?.taxReturnResult,
        instantRefund: instantRefundRequest,
        fromFedUser: data?.taxReturnFilingType === taxReturnFilingType.complementary,
        isFlexNotMandatory,
      });
    } else if (redirectToFlexLite && plan === FRONT_PLANS.BASIC) {
      history.push(routes.FLEX_LITE, {
        taxReturnType: data?.taxReturnType,
        taxReturnResult: data?.taxReturnResult,
        fromFedUser: data?.taxReturnFilingType === taxReturnFilingType.complementary,
      });
    } else if (havePreviousYearsAndIsLessThan100 && plan === FRONT_PLANS.FLEX) {
      history.push(routes.FILING);
    } else {
      const isInstantRefund = instantRefundRequest;
      handleRedirectUser({
        plan,
        data,
        isInstantRefund,
        isExtendedAndIsToReturn: resultIsLessThan100AndNotHavePreviousYears,
        isUserToPro,
        needEfirmaUser: data?.needEfirmaUser,
      });
    }
  };
  const hasPreviousYearAndPlan =
    havePreviousYearsAndIsLessThan100 &&
    plan !== FRONT_PLANS.BASIC &&
    plan !== FRONT_PLANS.FLEX_LITE;
  const isUserFlexAndRenderCrossSellAA = showPreviousYearBanner && !resultLessThan100;
  const renderCrossSellAA = !havePreviousYearsAndIsLessThan100 || hasPreviousYearAndPlan;

  useEffect(() => {
    if (data) {
      sendTaxReturnResultViewEvent(
        data,
        previousYearInfo,
        !renderCrossSellAA || isUserFlexAndRenderCrossSellAA
      );
    }
  }, [data]);

  return (
    <Styled.Box type={data?.taxReturnType}>
      <Styled.BoxContent direction="column" align="center" justify="center" gap="dimension_16">
        {data?.taxReturnFilingType === taxReturnFilingType.complementary && (
          <Banner
            id="complementary-banner"
            label={t('Result.complementaryBanner')}
            mode="warning"
            hasIcon
            titleSize={Sizes.S}
            layout="embed"
            position="vertically"
            size={Sizes.S}
          />
        )}
        {renderCrossSellAA && !isFlexNotMandatory ? (
          <ResultInfo data={data} isToPayLite={false} />
        ) : isFlexNotMandatory && previousYearInfo ? (
          <FlexsUsersResult daInfo={data} pyInfo={previousYearInfo} />
        ) : (
          <ResultLessRefund data={data} />
        )}
        {!flowEnabled ? (
          <Buttons.Classic
            label={t('Result.toReturn.button', {
              context: resultIsLessThan100AndNotHavePreviousYears && 'free',
            })}
            mode="primary"
            size="m"
            onClick={handleOnClick}
            disabled={loadingInstantRefund}
          />
        ) : (
          <Styled.ButtonsContainer>
            <Buttons.Classic
              label={t('Result.contact.asist')}
              mode="success"
              size="s"
              onClick={showChat}
            />
          </Styled.ButtonsContainer>
        )}
        {showPreviousYearBanner && !resultLessThan100 && !isFlexNotMandatory && (
          <PreviousYearInfo />
        )}
      </Styled.BoxContent>
    </Styled.Box>
  );
};

export default ResultBox;
